import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiCheck } from "react-icons/fi";
import Dropdown from "./dropdown";
import {
  SearchableSingleSelectDropdownOption,
  SearchableSingleSelectDropdownProps,
} from "./types";

const normalizeSearchValue = (value: string) => value.trim().toLowerCase();

const SearchableSingleSelectDropdown = <ValueType extends string>({
  ariaLabel,
  buttonClassName,
  className,
  describedBy,
  disabled = false,
  emptyOptionLabel = "No selection",
  emptyResultsLabel = "No matching options",
  id,
  isLoading = false,
  labelledBy,
  menuClassName,
  onChange,
  onCustomValue,
  customOptionLabel = (text) => `Use "${text}"`,
  onSearchTermChange,
  options,
  placeholder = "Select an option",
  searchPlaceholder = "Search...",
  selectedLabel,
  portal = false,
  value,
}: SearchableSingleSelectDropdownProps<ValueType>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const setSearchInputRef = useCallback(
    (node: HTMLInputElement | null) => {
      searchInputRef.current = node;

      if (node && isOpen) {
        node.focus({ preventScroll: true });
      }
    },
    [isOpen]
  );

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value) ?? null,
    [options, value]
  );

  const filteredOptions = useMemo(() => {
    const normalizedSearchTerm = normalizeSearchValue(searchTerm);
    if (!normalizedSearchTerm) {
      return options;
    }

    return options.filter((option) =>
      normalizeSearchValue(
        `${option.label} ${option.searchText || ""}`
      ).includes(normalizedSearchTerm)
    );
  }, [options, searchTerm]);

  const resolvedSelectedLabel = selectedOption?.label || selectedLabel;
  const customValue = searchTerm.trim();
  const showCustomOption =
    !!onCustomValue &&
    !!customValue &&
    !options.some(
      (option) =>
        normalizeSearchValue(option.value) === normalizeSearchValue(customValue)
    );

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
      onSearchTermChange?.("");
    }
  }, [isOpen, onSearchTermChange]);

  const handleSelect = (nextValue: ValueType | "") => {
    onChange(nextValue);
    setIsOpen(false);
  };

  return (
    <Dropdown
      portal={portal}
      buttonAriaDescribedBy={describedBy}
      buttonAriaLabelledBy={labelledBy}
      label={
        <span
          className={`block truncate text-left ${
            resolvedSelectedLabel ? "text-blue-300" : "text-grey-200"
          }`}
        >
          {resolvedSelectedLabel || placeholder}
        </span>
      }
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      className={className}
      buttonDisabled={disabled}
      buttonStyle={`min-h-[42px] items-center border-slate-300 bg-white text-left shadow-sm transition hover:border-slate-400 ${
        buttonClassName || ""
      }`}
      menuClassName={`mt-2 w-full min-w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.12)] ${
        menuClassName || ""
      }`}
    >
      <div
        className={
          portal
            ? "flex max-h-[min(26rem,var(--dropdown-available-height,26rem))] flex-col overflow-hidden"
            : "max-h-[18rem] overflow-hidden"
        }
      >
        <div
          className="shrink-0 border-b border-slate-100 px-3 py-2"
          onKeyDown={(event) => {
            event.stopPropagation();
            if (event.key === "Escape") {
              event.preventDefault();
              setIsOpen(false);
            }
          }}
        >
          <input
            ref={setSearchInputRef}
            id={id}
            type="text"
            aria-label={ariaLabel}
            aria-describedby={describedBy}
            value={searchTerm}
            placeholder={searchPlaceholder}
            onChange={(event) => {
              setSearchTerm(event.target.value);
              onSearchTermChange?.(event.target.value);
            }}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => {
              if (event.key !== "Enter" || !onCustomValue) return;
              event.preventDefault();
              if (!customValue) return;
              const match = options.find(
                (option) =>
                  normalizeSearchValue(option.value) ===
                  normalizeSearchValue(customValue)
              );
              if (match) handleSelect(match.value);
              else {
                onCustomValue(customValue);
                setIsOpen(false);
              }
            }}
            className="form-input block w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-blue-300 focus:border-blue-400 focus:bg-white focus:outline-none"
          />
        </div>

        <div
          className={`${
            portal ? "max-h-80" : "max-h-[13rem]"
          } overflow-auto py-1`}
          role="listbox"
        >
          <button
            type="button"
            className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition ${
              value === ""
                ? "bg-slate-100 text-blue-400"
                : "text-blue-300 hover:bg-slate-50"
            }`}
            onClick={() => handleSelect("")}
          >
            <span className="truncate">{emptyOptionLabel}</span>
            {value === "" ? <FiCheck className="shrink-0" size={16} /> : null}
          </button>

          {isLoading ? (
            <div className="px-3 py-2 text-sm text-grey-200">Loading...</div>
          ) : filteredOptions.length === 0 ? (
            <div className="px-3 py-2 text-sm text-grey-200">
              {emptyResultsLabel}
            </div>
          ) : (
            filteredOptions.map(
              (option: SearchableSingleSelectDropdownOption<ValueType>) => (
                <button
                  key={option.value}
                  type="button"
                  className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition ${
                    option.value === value
                      ? "bg-slate-100 text-blue-400"
                      : "text-blue-300 hover:bg-slate-50"
                  }`}
                  onClick={() => handleSelect(option.value)}
                >
                  <span className="truncate">{option.label}</span>
                  {option.value === value ? (
                    <FiCheck className="shrink-0" size={16} />
                  ) : null}
                </button>
              )
            )
          )}
          {showCustomOption && (
            <button
              type="button"
              className="flex w-full border-t border-grey-100 px-3 py-2 text-left text-sm text-blue-300 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-100"
              onClick={() => {
                onCustomValue?.(customValue);
                setIsOpen(false);
              }}
            >
              {customOptionLabel(customValue)}
            </button>
          )}
        </div>
      </div>
    </Dropdown>
  );
};

export default SearchableSingleSelectDropdown;
